import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, Save, Loader2, X, Plus, ExternalLink } from 'lucide-react';
import { sectorsApi, brandsApi } from '../../services/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Checkbox } from '../../components/ui/checkbox';
import ImageUpload from '../../components/admin/ImageUpload';
import RichTextEditor from '../../components/admin/RichTextEditor';

const AdminSectorEdit = () => {
  const { sectorId } = useParams();
  const [sector, setSector] = useState(null);
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [sectorRes, brandsRes] = await Promise.all([
          sectorsApi.get(sectorId),
          brandsApi.getAll(),
        ]);
        setSector(sectorRes.data);
        setBrands(brandsRes.data);
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [sectorId]);

  const handleChange = (field, value) => {
    setSector((prev) => ({ ...prev, [field]: value }));
  };

  const handleHeroImageChange = (index, value) => {
    const newImages = [...(sector.heroImages || ['', '', ''])];
    newImages[index] = value;
    setSector((prev) => ({ ...prev, heroImages: newImages }));
  };

  const handleHeroImageLinkChange = (index, value) => {
    const newLinks = [...(sector.heroImageLinks || ['', '', ''])];
    newLinks[index] = value;
    setSector((prev) => ({ ...prev, heroImageLinks: newLinks }));
  };

  const handleBrandToggle = (brandId) => {
    const currentBrands = sector.selectedBrands || [];
    const exists = currentBrands.find(b => b.brandId === brandId);
    
    if (exists) {
      // Remove brand
      setSector((prev) => ({
        ...prev,
        selectedBrands: currentBrands.filter(b => b.brandId !== brandId)
      }));
    } else {
      // Add brand with empty custom URL
      setSector((prev) => ({
        ...prev,
        selectedBrands: [...currentBrands, { brandId, customUrl: '' }]
      }));
    }
  };

  const handleBrandUrlChange = (brandId, customUrl) => {
    const currentBrands = sector.selectedBrands || [];
    setSector((prev) => ({
      ...prev,
      selectedBrands: currentBrands.map(b => 
        b.brandId === brandId ? { ...b, customUrl } : b
      )
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    setMessage('');
    try {
      await sectorsApi.update(sectorId, {
        name: sector.name,
        title: sector.title,
        introText: sector.introText,
        heroImages: sector.heroImages || [],
        heroImageLinks: sector.heroImageLinks || [],
        selectedBrands: sector.selectedBrands || [],
      });
      setMessage('Sector saved successfully!');
      setTimeout(() => setMessage(''), 3000);
    } catch (error) {
      setMessage('Error saving sector');
      console.error('Error:', error);
    } finally {
      setSaving(false);
    }
  };

  const isBrandSelected = (brandId) => {
    return (sector.selectedBrands || []).some(b => b.brandId === brandId);
  };

  const getBrandCustomUrl = (brandId) => {
    const brand = (sector.selectedBrands || []).find(b => b.brandId === brandId);
    return brand?.customUrl || '';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
      </div>
    );
  }

  if (!sector) {
    return <div>Sector not found</div>;
  }

  // Ensure we always have 3 image slots
  const heroImages = sector.heroImages || [];
  while (heroImages.length < 3) heroImages.push('');

  return (
    <div data-testid="admin-sector-edit-page">
      <div className="flex items-center justify-between mb-8">
        <div className="flex items-center">
          <Link to="/admin/sectors" className="mr-4 text-gray-400 hover:text-gray-600">
            <ArrowLeft className="h-5 w-5" />
          </Link>
          <div>
            <h1 className="text-2xl font-semibold text-gray-900">{sector.name}</h1>
            <p className="text-gray-500 text-sm">Edit sector landing page</p>
          </div>
        </div>
        <div className="flex items-center gap-4">
          {message && (
            <span className={`text-sm ${message.includes('Error') ? 'text-red-600' : 'text-green-600'}`}>
              {message}
            </span>
          )}
          <Button onClick={handleSave} disabled={saving} data-testid="save-sector-btn">
            {saving ? (
              <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Saving...</>
            ) : (
              <><Save className="h-4 w-4 mr-2" />Save Changes</>
            )}
          </Button>
        </div>
      </div>

      <div className="space-y-8">
        {/* Basic Info */}
        <div className="bg-white rounded-lg shadow-sm p-6">
          <h2 className="text-lg font-medium text-gray-900 mb-6">Sector Information</h2>
          
          <div className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="name">Sector Name</Label>
              <Input
                id="name"
                value={sector.name || ''}
                onChange={(e) => handleChange('name', e.target.value)}
                placeholder="e.g., Outdoor + Architectural"
                data-testid="sector-name-input"
              />
              <p className="text-xs text-gray-500">
                This appears in the navigation dropdown menu
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="title">Page Title</Label>
              <Input
                id="title"
                value={sector.title || ''}
                onChange={(e) => handleChange('title', e.target.value)}
                placeholder={`${sector.name} Lighting`}
                data-testid="sector-title-input"
              />
              <p className="text-xs text-gray-500">
                This appears as the main heading on the sector page
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="introText">Introduction Text</Label>
              <RichTextEditor
                id="introText"
                rows={4}
                value={sector.introText || ''}
                onChange={(value) => handleChange('introText', value)}
                placeholder="Describe this sector and your lighting solutions..."
              />
              <p className="text-xs text-gray-500">
                This text appears below the hero images. Use the toolbar for formatting.
              </p>
            </div>
          </div>
        </div>

        {/* Hero Images */}
        <div className="bg-white rounded-lg shadow-sm p-6">
          <h2 className="text-lg font-medium text-gray-900 mb-2">Hero Images</h2>
          <p className="text-gray-500 text-sm mb-6">
            Add 3 images that will be displayed across the top of the sector page. Each image can link to a URL when clicked.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[0, 1, 2].map((index) => {
              const heroImageLinks = sector.heroImageLinks || ['', '', ''];
              return (
                <div key={index} className="space-y-3">
                  <ImageUpload
                    label={`Image ${index + 1}`}
                    value={heroImages[index] || ''}
                    onChange={(url) => handleHeroImageChange(index, url)}
                    previewHeight="aspect-video"
                  />
                  <div className="space-y-1">
                    <Label htmlFor={`link-${index}`} className="text-xs text-gray-600">
                      Click URL (optional)
                    </Label>
                    <Input
                      id={`link-${index}`}
                      value={heroImageLinks[index] || ''}
                      onChange={(e) => handleHeroImageLinkChange(index, e.target.value)}
                      placeholder="https://..."
                      className="text-sm"
                      data-testid={`hero-image-link-${index}`}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Brand Selection */}
        <div className="bg-white rounded-lg shadow-sm p-6">
          <h2 className="text-lg font-medium text-gray-900 mb-2">Partner Brands</h2>
          <p className="text-gray-500 text-sm mb-6">
            Select brands to display on this sector page. You can add a custom URL for each brand.
          </p>

          <div className="space-y-4">
            {brands.map((brand) => {
              const isSelected = isBrandSelected(brand.id);
              const customUrl = getBrandCustomUrl(brand.id);

              return (
                <div
                  key={brand.id}
                  className={`border rounded-lg p-4 transition-colors ${
                    isSelected ? 'border-blue-200 bg-blue-50' : 'border-gray-200'
                  }`}
                >
                  <div className="flex items-start gap-4">
                    <Checkbox
                      id={`brand-${brand.id}`}
                      checked={isSelected}
                      onCheckedChange={() => handleBrandToggle(brand.id)}
                      data-testid={`brand-checkbox-${brand.id}`}
                    />
                    
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-3">
                        {brand.logo && (
                          <img
                            src={brand.logo}
                            alt={brand.name}
                            className="h-6 max-w-[80px] object-contain"
                          />
                        )}
                        <label
                          htmlFor={`brand-${brand.id}`}
                          className="font-medium text-gray-900 cursor-pointer"
                        >
                          {brand.name}
                        </label>
                        <span className="text-sm text-gray-500">{brand.country}</span>
                      </div>

                      {isSelected && (
                        <div className="mt-3 pl-0">
                          <Label htmlFor={`url-${brand.id}`} className="text-xs text-gray-600">
                            Custom URL (optional)
                          </Label>
                          <div className="flex gap-2 mt-1">
                            <Input
                              id={`url-${brand.id}`}
                              value={customUrl}
                              onChange={(e) => handleBrandUrlChange(brand.id, e.target.value)}
                              placeholder={`Default: /brand/${brand.id}`}
                              className="text-sm"
                              data-testid={`brand-url-${brand.id}`}
                            />
                            {customUrl && (
                              <a
                                href={customUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="flex items-center px-3 text-gray-500 hover:text-gray-700"
                              >
                                <ExternalLink className="h-4 w-4" />
                              </a>
                            )}
                          </div>
                          <p className="text-xs text-gray-500 mt-1">
                            Leave empty to link to the brand page, or enter an external URL
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Preview Link */}
        <div className="bg-gray-50 rounded-lg p-4 flex items-center justify-between">
          <div>
            <p className="text-sm font-medium text-gray-900">Preview this sector page</p>
            <p className="text-sm text-gray-500">See how it looks to visitors</p>
          </div>
          <a
            href={`/sector/${sector.id}`}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center px-4 py-2 bg-gray-900 text-white text-sm font-medium rounded hover:bg-gray-800 transition-colors"
          >
            View Page
            <ExternalLink className="ml-2 h-4 w-4" />
          </a>
        </div>
      </div>
    </div>
  );
};

export default AdminSectorEdit;
